import useSound from "use-sound";
import { Box, Button, Slider, Stack } from "@mui/material";
import {
  getRouletteBlack,
  getRouletteRed,
  MODULO_COINFLIP,
  MODULO_ETHEROLL,
  MODULO_ROLLADICE,
  MODULO_ROULETTE,
  MODULO_TWODICE,
} from "../../../games";
import { getRange, getRouletteColor } from "../../../utils";
import { useContext } from "react";
import SessionContext from "../../../contexts/session";
import { useTranslation, Trans } from "react-i18next";

const controlSpacing = 0.5;

export default function GameInputs({ game, inputs, setInputs }) {
  const { t } = useTranslation();
  const handleAddInput = (newInput) =>
    function () {
      if (game)
        setInputs((inputs) => {
          if (game.modulo === MODULO_COINFLIP) {
            return inputs[0] === 1 ? [2] : [1];
          }
          const matching = inputs.find((input) => {
            return input === newInput;
          });
          if (matching >= 0) {
            return inputs.filter((input) => input !== newInput);
          } else {
            return [...inputs, newInput];
          }
        });
    };

  const handleAddInputs = (newInputs) => {
    setInputs((inputs) => {
      const notFound = newInputs.filter(
        (newInput) => !inputs.includes(newInput)
      );
      if (notFound.length === 0) {
        return inputs.filter((input) => !newInputs.includes(input));
      }
      return [...inputs, ...notFound];
    });
  };

  function handleSliderChange(event, newValue) {
    setInputs([newValue]);
  }

  return (
    <>
      {game.modulo === MODULO_COINFLIP && (
        <Stack direction="row" spacing={controlSpacing}>
          {[t("heads"), t("tails")].map((side, index) => {
            const betMask = index + 1;
            return (
              <ButtonControl
                key={side}
                text={t(side)}
                onClick={handleAddInput(betMask)}
                isSelected={inputs.includes(betMask)}
              />
            );
          })}
        </Stack>
      )}
      {game.modulo === MODULO_ROLLADICE && (
        <Stack spacing={controlSpacing}>
          {[
            [0, 1, 2],
            [3, 4, 5],
          ].map((diceRow, diceRowIndex) => (
            <Stack key={diceRowIndex} direction="row" spacing={controlSpacing}>
              {diceRow.map((dice) => (
                <div key={dice} className="helper-dice">
                  <ButtonControl
                    text={dice + 1}
                    onClick={handleAddInput(dice)}
                    isSelected={inputs.includes(dice)}
                  />
                </div>
              ))}
            </Stack>
          ))}
        </Stack>
      )}
      {game.modulo === MODULO_TWODICE && (
        <Stack spacing={controlSpacing}>
          {[
            [2, 3, 4, 5],
            [6, 7, 8, 9],
            [10, 11, 12],
          ].map((sumRow, sumRowIndex) => (
            <Stack key={sumRowIndex} direction="row" spacing={controlSpacing}>
              {sumRow.map((sum) => (
                <div key={sum} className="helper-big">
                  <ButtonControl
                    text={sum}
                    onClick={handleAddInput(sum)}
                    isSelected={inputs.includes(sum)}
                  />
                </div>
              ))}
            </Stack>
          ))}
        </Stack>
      )}
      {game.modulo === MODULO_ROULETTE && (
        <Box
          direction="row"
          sx={{
            aspectRatio: { xs: "5/14", sm: "14/5" },
            display: "grid",
            width: "100%",
            maxWidth: { xs: "200px", sm: "750px" },
            // gridTemplateRows: "repeat(5, 1fr)",
            gridTemplateAreas: {
              xs: `'. . number0 number0 number0'
              'range0118 range0112 number1 number2 number3'
              'range0118 range0112 number4 number5 number6'
              'parityEven range0112 number7 number8 number9'
              'parityEven range0112 number10 number11 number12'
              'colorRed range1324 number13 number14 number15'
              'colorRed range1324 number16 number17 number18'
              'colorBlack range1324 number19 number20 number21'
              'colorBlack range1324 number22 number23 number24'
              'parityOdd range2536 number25 number26 number27'
              'parityOdd range2536 number28 number29 number30'
              'range1936 range2536 number31 number32 number33'
              'range1936 range2536 number34 number35 number36'
              '. . row3 row2 row1'`,
              sm: `'number0 number3 number6 number9 number12 number15 number18 number21 number24 number27 number30 number33 number36 row1'
        'number0 number2 number5 number8 number11 number14 number17 number20 number23 number26 number29 number32 number35 row2'
        'number0 number1 number4 number7 number10 number13 number16 number19 number22 number25 number28 number31 number34 row3'
        '. range0112 range0112 range0112 range0112 range1324 range1324 range1324 range1324 range2536 range2536 range2536 range2536 .'
        '. range0118 range0118 parityEven parityEven colorRed colorRed colorBlack colorBlack parityOdd parityOdd range1936 range1936 .'`,
            },
          }}
        >
          {getRange(0, 36).map((rouletteNumber) => (
            <ButtonRoulette
              className={"helpobigo"}
              key={rouletteNumber}
              n={rouletteNumber}
              isSelected={inputs.includes(rouletteNumber)}
              onClick={handleAddInput(rouletteNumber)}
            />
          ))}
          <ButtonRoulette
            text="2:1"
            gridArea="row1"
            onClick={() => handleAddInputs(getRange(1, 12).map((i) => i * 3))}
          />
          <ButtonRoulette
            text="2:1"
            gridArea="row2"
            onClick={() =>
              handleAddInputs(getRange(1, 12).map((i) => i * 3 - 1))
            }
          />
          <ButtonRoulette
            text="2:1"
            gridArea="row3"
            onClick={() =>
              handleAddInputs(getRange(1, 12).map((i) => i * 3 - 2))
            }
          />
          <ButtonRoulette
            text="1 to 12"
            gridArea="range0112"
            onClick={() => handleAddInputs(getRange(1, 12))}
          />
          <ButtonRoulette
            text="13 to 24"
            gridArea="range1324"
            onClick={() => handleAddInputs(getRange(13, 24))}
          />
          <ButtonRoulette
            text="25 to 36"
            gridArea="range2536"
            onClick={() => handleAddInputs(getRange(25, 36))}
          />
          <ButtonRoulette
            text="1 to 18"
            gridArea="range0118"
            onClick={() => handleAddInputs(getRange(1, 18))}
          />
          <ButtonRoulette
            text="19 to 36"
            gridArea="range1936"
            onClick={() => handleAddInputs(getRange(19, 36))}
          />
          <ButtonRoulette
            text="Even"
            gridArea="parityEven"
            onClick={() =>
              handleAddInputs(getRange(1, 36).filter((i) => i % 2 === 0))
            }
          />
          <ButtonRoulette
            text="Odd"
            gridArea="parityOdd"
            onClick={() =>
              handleAddInputs(getRange(1, 36).filter((i) => i % 2))
            }
          />
          <ButtonRoulette
            text="◆"
            n={1}
            gridArea="colorRed"
            onClick={() => handleAddInputs(getRouletteRed())}
          />
          <ButtonRoulette
            text="◆"
            gridArea="colorBlack"
            onClick={() => handleAddInputs(getRouletteBlack())}
          />
        </Box>
      )}
      {game.modulo === MODULO_ETHEROLL && (
        <Box sx={{ width: "80%" }}>
          <Slider
            className="bigobig"
            color="success"
            valueLabelDisplay="on"
            min={1}
            max={97}
            sx={{
              // height: 8,
              // width: "100%",
              "& .MuiSlider-rail": {
                backgroundColor: "error.main",
              },
            }}
            value={inputs[0]}
            onChange={handleSliderChange}
            marks={[
              {
                value: 1,
                label: "1%",
              },
              {
                value: 25,
                label: "25%",
              },
              {
                value: 50,
                label: "50%",
              },
              {
                value: 75,
                label: "75%",
              },
              {
                value: 97,
                label: "97%",
              },
            ]}
          />
        </Box>
      )}
    </>
  );
}

function ButtonControl({ text, onClick, isSelected }) {
  const { volume } = useContext(SessionContext);
  const [playSelect] = useSound("/audio/select.wav", { volume });

  function handleClick() {
    onClick();
    playSelect();
  }

  return (
    <Button
      variant={isSelected ? "contained" : "outlined"}
      onClick={handleClick}
      sx={{ borderRadius: 0 }}
    >
      {text}
    </Button>
  );
}

function ButtonRoulette({ className, n, text, gridArea, onClick, isSelected }) {
  const { volume } = useContext(SessionContext);

  const [playSelect] = useSound("/audio/select.wav", { volume });

  function handleClick() {
    onClick();
    playSelect();
  }

  return (
    <Box
      className={className}
      onClick={handleClick}
      sx={{
        background: getRouletteColor(n),
        gridArea: gridArea || `number${n}`,
        position: `relative`,
        borderRadius: 0.5,
        // p: 2,
        border: "2px solid",
        borderColor: text
          ? "rgba(255,255,255,0.1)"
          : isSelected
          ? "#40ff00"
          : "transparent",
        cursor: "pointer",
        "&:hover": {
          borderColor: isSelected ? "#40ff00" : "white",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          writingMode: { xs: text ? "vertical-lr" : "", sm: "inherit" },
          color: isSelected ? "#40ff00" : "white",
        }}
      >
        {text || n}
      </Box>
    </Box>
  );
}
